import {
  LayoutDashboard, CalendarRange, Boxes, Cpu, ShoppingCart,
  Truck, Users, PackageCheck, Network, Lock, Rocket, Hash,
} from "lucide-react";

// Auto-generated in-app user guide. Each section renders as searchable content.
// block types: p (paragraph), ul (bullets), steps (numbered), tip (highlight box).
// `image` is a labelled screenshot shown at the top of the section.
export const HELP_SECTIONS = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: Rocket,
    keywords: "intro overview roles login admin planner operator navigation tabs",
    image: { src: "/help/help_dashboard.jpg", label: "The Operations Overview — tab bar under the page title lets you move between every area." },
    blocks: [
      { t: "p", c: "H. K. INDUSTRIES Operations is a real-time production planning system. Everything updates live across users — when someone confirms an order or logs production, your screen refreshes automatically (watch the Live indicator in the top bar)." },
      { t: "p", c: "There are three roles, each with its own view:" },
      { t: "ul", c: [
        "Admin — full control: settings, users, data import/export, and every operational action.",
        "Planner — schedules, sales orders, purchasing and production planning.",
        "Operator — a focused, read-mostly floor view scoped to only the lines and jobs assigned to them (see 'My Stages').",
      ] },
      { t: "p", c: "Move between areas using the tab bar under the page title (Dashboard, Schedule, BOM, Planning, Sales, and more)." },
    ],
  },
  {
    id: "dashboard",
    title: "Dashboard",
    icon: LayoutDashboard,
    keywords: "kpi metrics shortage tile spare reorder alerts analytics charts",
    image: { src: "/help/help_dashboard.jpg", label: "Dashboard tab — KPI cards, shortage tile and quick actions across the top." },
    blocks: [
      { t: "p", c: "The Dashboard is your at-a-glance health check. KPI cards summarise active orders, capacity and delays — click a delayed KPI to jump straight to the delayed orders on the Schedule." },
      { t: "ul", c: [
        "Shortage Tile — materials that will run short for confirmed orders due this week, with a shortcut to the Purchase List.",
        "Spare Reorder Alerts — a banner warning admins when spare parts drop to or below their reorder level.",
        "Analytics — trend charts for throughput and on-time performance.",
      ] },
    ],
  },
  {
    id: "schedule",
    title: "Schedule, Gantt & Stage-Gate",
    icon: CalendarRange,
    keywords: "schedule gantt production health wip pipeline stage gate reschedule drag timeline downtime",
    image: { src: "/help/help_schedule.jpg", label: "Schedule tab — the Production Health table with per-order Stage-Gate status; the Gantt sits below it." },
    blocks: [
      { t: "p", c: "The Schedule tab shows Production Health at the top and the Gantt timeline below it." },
      { t: "p", c: "Each order runs through a sequential Stage-Gate pipeline — a stage cannot start until the previous one is complete. Expand an order to see its WIP Pipeline (the stage boxes) and log, ship or receive each stage in order." },
      { t: "steps", c: [
        "Find the order in the Production Health table and expand it.",
        "Work the current stage — Log progress for in-house stages; Ship / Receive for outsourced (OSP) stages.",
        "Completing a stage automatically opens the gate to the next one.",
        "Use the Gantt below to drag orders to reschedule; blocked days and downtime are shaded.",
      ] },
      { t: "tip", c: "The Stage History drawer (clock icon on a row) shows a full audit trail of who did what and when for every stage." },
    ],
  },
  {
    id: "bom",
    title: "BOM & Materials Catalog",
    icon: Boxes,
    keywords: "bill of materials recipe components nested stock reserved where used build to stock duplicate archive spare raw manufactured",
    image: { src: "/help/help_bom.jpg", label: "BOM tab — item cards showing type, in-stock, reserved badges and nested recipes." },
    blocks: [
      { t: "p", c: "The BOM tab is your materials catalog. Every item is one of four categories: Raw Material, Brought Out, Mfg. Spares, or Manufactured. Manufactured items have a recipe (components) that can nest sub-assemblies several levels deep." },
      { t: "ul", c: [
        "In stock — current on-hand quantity; turns amber when at/below the reorder level.",
        "Reserved — finished goods committed to confirmed sales orders. Hover the reserved badge to see exactly which orders are holding that stock.",
        "Where used — for a raw material, see every product whose recipe consumes it, plus upcoming demand.",
        "Build to Stock — record produced units of a manufactured item; it adds finished stock and consumes the recipe materials.",
      ] },
      { t: "steps", c: [
        "Click Add Item to create a product; use Duplicate to start from an existing item's recipe.",
        "Add components and production stages (keyboard-friendly: Enter adds a row, Alt+Arrow moves a row).",
        "Adjust stock with the warehouse icon, or archive an item to hide it while keeping history.",
      ] },
    ],
  },
  {
    id: "planning",
    title: "Production Planning",
    icon: Hash,
    keywords: "planning need to produce demand available to plan reserved in production create order",
    image: { src: "/help/help_planning.jpg", label: "Planning tab — Pending Demand, Reserved, Available to Plan, In Production and Need to Produce per product." },
    blocks: [
      { t: "p", c: "The Planning tab tells you what to make. For each finished good it nets open sales demand against what you already have and what's already being made." },
      { t: "ul", c: [
        "Pending Demand — quantity ordered across open sales orders (Pending + Confirmed).",
        "Reserved — units already committed to confirmed orders.",
        "Available to Plan — on-hand stock minus reserved.",
        "In Production — quantity already on open production orders.",
        "Need to Produce — the shortfall you should schedule; click Create Production Order to act on it.",
      ] },
      { t: "tip", c: "Use 'Order everything short' to raise every purchase order needed to cover material shortfalls in one click." },
    ],
  },
  {
    id: "sales",
    title: "Sales Orders & Reservations",
    icon: PackageCheck,
    keywords: "sales order customer confirm fulfil cancel reservation lock guard fulfil guard pdf delivery note ai po import",
    image: { src: "/help/help_sales.jpg", label: "Sales tab — orders with status, Fulfil Guard badges (Ready / Partial / Awaiting Production) and actions." },
    blocks: [
      { t: "p", c: "Record customer orders for finished goods here. Only top-level manufactured products can be sold." },
      { t: "steps", c: [
        "Create a sales order (Pending). A matching production order is created automatically so it appears on the Gantt.",
        "Confirm it — this reserves the finished-goods stock and emails the customer a branded confirmation PDF.",
        "Fulfil it once stock is ready — this ships the goods, deducts stock and releases the reservation.",
        "Cancel (before fulfilment) releases any reservation held by the order.",
      ] },
      { t: "ul", c: [
        "Fulfil Guard — the coloured badge shows whether every line has enough stock (green = ready, amber = partial, grey = awaiting production).",
        "Reservation Lock Guard — if confirming would reserve stock already committed to other confirmed orders, you'll get a warning showing how much is over-committed, with a 'Reserve anyway' option.",
        "AI PO Import — turn a customer's PO document into a draft sales order automatically.",
      ] },
    ],
  },
  {
    id: "purchasing",
    title: "Purchasing & Suppliers",
    icon: ShoppingCart,
    keywords: "purchase order po approval receive supplier reorder pending approval lead time",
    image: { src: "/help/help_purchasing.jpg", label: "Purchasing tab — purchase orders with status, approval audit, expected dates and Receive controls." },
    blocks: [
      { t: "p", c: "Raise and track purchase orders to replenish materials. Depending on your role, POs may need admin approval before they're placed." },
      { t: "steps", c: [
        "Raise a PO from a shortage (Planning, Sales or Where-Used), or manually in the Purchasing tab.",
        "Admins approve or reject pending POs; approved POs show incoming quantities as 'expected' on the item.",
        "Receive stock against the PO as deliveries arrive — full or partial.",
      ] },
      { t: "p", c: "The Suppliers tab manages vendor details and preferred suppliers used when auto-grouping purchase orders." },
    ],
  },
  {
    id: "osp",
    title: "Outside Processing (OSP)",
    icon: Truck,
    keywords: "osp outsource gate pass vendor overdue ship receive subcontract",
    image: { src: "/help/help_osp.jpg", label: "OSP tab — outside-processing vendors and open gate passes, with overdue alerts." },
    blocks: [
      { t: "p", c: "OSP stages send work to an outside vendor. Shipping an OSP stage creates a Gate Pass; receiving it back records the return." },
      { t: "ul", c: [
        "Vendor Overdue Alerts — a banner flags gate passes that are late back from a vendor.",
        "Manage vendors and review all open gate passes from the OSP tab.",
      ] },
    ],
  },
  {
    id: "inventory-system",
    title: "Inventory Log & System Map",
    icon: Network,
    keywords: "inventory log audit movements system map overview global inventory who changed stock",
    image: { src: "/help/help_system.jpg", label: "System Map — a bird's-eye view of overall system health with jump-to shortcuts." },
    blocks: [
      { t: "p", c: "The Inventory Log records every stock movement — who changed what, when and why — with CSV export and filters." },
      { t: "p", c: "The System Map gives a bird's-eye view of overall system health and lets you jump to problem areas (e.g. low stock)." },
    ],
  },
  {
    id: "conventions",
    title: "Numbers, Units & Currency",
    icon: Lock,
    keywords: "formatting en-in indian numbering currency rupee kg decimals units precision",
    blocks: [
      { t: "p", c: "The whole app follows Indian conventions so figures read the way you expect:" },
      { t: "ul", c: [
        "Numbers use the Indian grouping (e.g. 2,00,000) and currency shows in ₹.",
        "Weights in kg show exactly 3 decimals; all other units show whole numbers.",
        "Because of this, fractional quantities are rejected for non-kg units.",
      ] },
    ],
  },
  {
    id: "roles-access",
    title: "Roles & Access",
    icon: Users,
    keywords: "roles permissions admin planner operator access users management line assignment",
    blocks: [
      { t: "p", c: "What you can do depends on your role. Admins manage users and assign operators to machine lines, which controls what each operator sees on their My Stages board and Schedule." },
      { t: "tip", c: "Read-only areas show a 'Read only' note where an action would otherwise appear." },
    ],
  },
];

export function searchHelp(sections, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return sections;
  const text = (s) =>
    [s.title, s.keywords, ...s.blocks.flatMap((b) => (Array.isArray(b.c) ? b.c : [b.c]))]
      .join(" ")
      .toLowerCase();
  return sections.filter((s) => text(s).includes(q));
}
